import { useEffect, useState } from "react"
import { useAuthStore } from "../stores/authStore"
import api from "../lib/api"

export interface CccdSidebarEvent {
  id: string
  name: string
  slug: string
  categorySlug: string | null
}

interface CccdEventsState {
  nationalEvents: CccdSidebarEvent[]
  regionalEvents: CccdSidebarEvent[]
  isLoading: boolean
}

const initialState: CccdEventsState = {
  nationalEvents: [],
  regionalEvents: [],
  isLoading: true,
}

/** Powers the "Eventos" sidebar group (Nacionales/Regionales) and the CCCD
 * category/competitors pages — see
 * openspec/changes/academy-staff-unrestricted-access. For a student, only
 * the events they have an active team in; for staff, every event marked
 * "destacado en Academy". `isLoading` lets callers distinguish "not fetched
 * yet" from "confirmed no events". */
export function useCccdEvents(): CccdEventsState {
  const user = useAuthStore((s) => s.user)
  const sessionType = useAuthStore((s) => s.sessionType)
  const [state, setState] = useState<CccdEventsState>(initialState)

  useEffect(() => {
    if (!user && sessionType !== "group") {
      setState({ ...initialState, isLoading: false })
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, isLoading: true }))
    api
      .get("/cccd/me/events")
      .then(({ data }) => {
        if (!cancelled) {
          setState({ ...(data.data as Omit<CccdEventsState, "isLoading">), isLoading: false })
        }
      })
      .catch(() => {
        // Best-effort — gated items/routes are omitted/denied on failure.
        if (!cancelled) setState({ ...initialState, isLoading: false })
      })

    return () => {
      cancelled = true
    }
  }, [user, sessionType])

  return state
}
